import React, { useEffect, useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";

/**
 * CustomCell — inline editor for a task's user-defined column value.
 *
 * Extracted from ProjectView so the scope dashboards (TL / CM / Department)
 * can render the same custom column cells without duplicating logic. `col` is
 * the raw column definition (id, label, type, options) as returned by
 * `/departments/:dept/columns`. Also used as a generic cell for the extra
 * base columns (ai_score, draft, check, notes) in ScopeView.
 */
export default function CustomCell({ col, value, onSave, taskId, members, lookupMap, tdProps = {}, handle }) {
  const [editing, setEditing] = useState(false);
  const [local, setLocal] = useState(value ?? "");
  useEffect(() => { setLocal(value ?? ""); }, [value]);
  const { className: extraClass = "", onClick: sheetOnClick, ...restTd } = tdProps;

  if (col.type === "dropdown") {
    return (
      <td className={extraClass} {...restTd}>
        <Select value={value || "none"} onValueChange={(v) => onSave(v === "none" ? "" : v)}>
          <SelectTrigger className="h-7 text-xs border-transparent hover:border-slate-200 bg-transparent" data-testid={`custom-${col.id}-${taskId}`}>
            <SelectValue placeholder="—" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">—</SelectItem>
            {(col.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
          </SelectContent>
        </Select>
        {handle}
      </td>
    );
  }

  if (col.type === "member") {
    // Iter 45.76 — resolve the assignee from the page's full user map when
    // they were removed from the project/team (and so dropped out of
    // `members`), so existing tasks keep showing their name instead of
    // falling back to "Unassigned".
    const poolMember = (members || []).find((m) => m.id === value);
    const fallbackMember = value && !poolMember ? lookupMap?.[value] : null;
    const selectedMember = poolMember || fallbackMember;
    return (
      <td className={extraClass} {...restTd}>
        <Select value={value || "none"} onValueChange={(v) => onSave(v === "none" ? "" : v)}>
          <SelectTrigger className="h-7 text-xs border-transparent hover:border-slate-200 bg-transparent" data-testid={`custom-${col.id}-${taskId}`}>
            {selectedMember
              ? <span className="truncate text-xs text-slate-700" title={fallbackMember ? "No longer assigned to this project" : undefined}>{selectedMember.name}</span>
              : <span className="text-slate-400 italic text-xs">Unassigned</span>}
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Unassigned</SelectItem>
            {(members || []).map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
          </SelectContent>
        </Select>
        {handle}
      </td>
    );
  }

  if (col.type === "bool") {
    return (
      <td className={`text-center ${extraClass}`} {...restTd}>
        <Checkbox checked={!!value} onCheckedChange={(v) => onSave(!!v)} data-testid={`custom-${col.id || col.key}-${taskId}`} />
        {handle}
      </td>
    );
  }

  if (editing) {
    return (
      <td className={`editing ${extraClass}`} {...restTd}>
        <input autoFocus type={col.type === "date" ? "date" : col.type === "number" ? "number" : (col.type === "url" ? "url" : "text")}
          value={local} onChange={(e) => setLocal(e.target.value)}
          onBlur={() => { setEditing(false); const norm = col.type === "number" ? (local === "" ? "" : Number(local)) : local; if (norm !== value) onSave(norm); }}
          onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); if (e.key === "Escape") { setLocal(value ?? ""); setEditing(false); } }}
          placeholder={col.type === "url" ? "https://…" : undefined}
          data-testid={`custom-${col.id || col.key}-${taskId}`} />
        {handle}
      </td>
    );
  }
  // Iter 42 — Link cells: when a URL is set, render the clickable link
  // with a small "edit" pencil that appears on hover so the value can be
  // changed without having to clear it first.
  if (col.type === "url" && value) {
    return (
      <td className={`${extraClass} group`} {...restTd}>
        <div className="flex items-center gap-1">
          <a href={String(value)} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-xs text-purple-600 hover:underline truncate flex-1" data-testid={`custom-${col.id}-link-${taskId}`}>
            {String(value)}
          </a>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setEditing(true); }}
            className="opacity-0 group-hover:opacity-100 text-[10px] text-slate-400 hover:text-purple-600 px-1"
            data-testid={`custom-${col.id}-edit-${taskId}`}
            title="Edit link"
          >
            edit
          </button>
        </div>
        {handle}
      </td>
    );
  }
  return (
    <td
      {...restTd}
      onClick={(e) => { sheetOnClick?.(e); setEditing(true); }}
      className={`cursor-text ${extraClass}`}
    >
      {(value !== "" && value !== null && value !== undefined) ? (
        <span className="text-slate-800">{String(value)}</span>
      ) : <span className="text-slate-300 text-xs italic">{col.type === "url" ? "paste link" : "click to edit"}</span>}
      {handle}
    </td>
  );
}
